import { useMemo, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { useLingui } from '@lingui/react'
import { msg } from '@lingui/macro'
import { resolveOptionDisplayLabel } from '../../utils/config-localization'

/**
 * Modale full-screen per selezione opzioni con ricerca (mobile).
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {Array<{ value?: string | number, label?: string, flag?: string }>} props.options
 * @param {string | number} [props.selectedValue]
 * @param {(opt: object) => void} props.onSelect
 * @param {boolean} [props.isCountryField]
 * @param {(a: unknown, b: unknown) => boolean} props.optionValueEquals
 */
export default function FormSelectSearchDialog({
  open,
  onClose,
  title,
  options = [],
  selectedValue,
  onSelect,
  isCountryField = false,
  optionValueEquals,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [query, setQuery] = useState('')
  const isDark = theme.palette.mode === 'dark'
  const headerBg = isDark ? theme.palette.newColors.gray : theme.palette.newColors.lightGray
  const accentColor = theme.palette.newColors.pink

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) {
      return options
    }
    return options.filter((opt) => {
      const label = String(resolveOptionDisplayLabel(opt) ?? '').toLowerCase()
      return label.includes(normalizedQuery)
    })
  }, [options, query])

  const handleClose = () => {
    setQuery('')
    onClose()
  }

  const handleSelect = (opt) => {
    onSelect(opt)
    setQuery('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 1,
          py: 1.5,
          gap: 1,
          bgcolor: headerBg,
          color: isDark ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: '0.9375rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label={_(msg`Close`)}
          onClick={handleClose}
          edge="end"
          size="small"
          sx={{ color: 'inherit' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          pt: 1.5,
          pb: 2,
          px: 2,
          overflow: 'hidden',
        }}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={_(msg`Search...`)}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: theme.palette.newColors.gray }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1,
            flexShrink: 0,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
              '&.Mui-focused fieldset': {
                borderColor: accentColor,
              },
            },
          }}
        />
        <Divider sx={{ mb: 0.5, opacity: 0.6 }} />
        <List sx={{ flex: 1, overflow: 'auto', mx: -1 }}>
          {filteredOptions.length === 0 ? (
            <Typography
              sx={{
                py: 3,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              {_(msg`No results`)}
            </Typography>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = optionValueEquals(opt.value, selectedValue)
              const displayLabel = resolveOptionDisplayLabel(opt)
              const optionKey = `${String(opt.value)}-${String(opt.label ?? '')}`

              return (
                <ListItemButton
                  key={optionKey}
                  selected={isSelected}
                  onClick={() => handleSelect(opt)}
                  sx={{
                    py: 1.25,
                    minHeight: 48,
                    borderRadius: 1.5,
                    mx: 0.5,
                    mb: 0.25,
                    fontSize: '0.9375rem',
                    '&.Mui-selected': {
                      bgcolor: isDark ? 'rgba(224, 40, 108, 0.18)' : 'rgba(224, 40, 108, 0.08)',
                      borderLeft: `3px solid ${accentColor}`,
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(224, 40, 108, 0.24)' : 'rgba(224, 40, 108, 0.12)',
                      },
                    },
                  }}
                >
                  {isCountryField && opt.value !== 0 ? (
                    <Box display="flex" alignItems="center" width="100%">
                      <Box
                        height="1.5rem"
                        border="1px solid #c4c4c4"
                        borderRadius={1}
                        sx={{
                          aspectRatio: '4/3',
                          backgroundImage: `url('${opt.flag}')`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center center',
                          flexShrink: 0,
                        }}
                      />
                      <Typography ml={1.5}>{displayLabel}</Typography>
                    </Box>
                  ) : (
                    displayLabel
                  )}
                </ListItemButton>
              )
            })
          )}
        </List>
      </DialogContent>
    </Dialog>
  )
}
